import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL;
const PROVINCES = ['ON', 'QC', 'BC', 'AB', 'MB', 'SK', 'NS', 'NB', 'NL', 'PE', 'NT', 'YT', 'NU'];

const ProductCard = ({ product, index, onBuy }) => {
  const [size, setSize] = useState('');
  const [quantity, setQuantity] = useState(1);

  const handleBuy = () => {
    if (product.sizes.length > 0 && !size) {
      toast.error('Please select a size first');
      return;
    }
    onBuy(product, size || null, quantity);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-shadow border border-neutral-100"
      data-testid={`merch-card-${product.product_id}`}
    >
      <div className="aspect-square bg-neutral-50 overflow-hidden">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-bold text-lg text-neutral-900" style={{ fontFamily: 'Outfit' }}>{product.name}</h3>
          <span className="text-lg font-bold text-[#FF3B30] whitespace-nowrap">${product.price.toFixed(0)} CAD</span>
        </div>
        <p className="text-sm text-neutral-500 mb-4">{product.description}</p>

        {product.sizes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid={`merch-sizes-${product.product_id}`}>
            {product.sizes.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                data-testid={`size-${product.product_id}-${s}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  size === s
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <select
            value={quantity}
            onChange={(e) => setQuantity(Number(e.target.value))}
            data-testid={`merch-qty-${product.product_id}`}
            className="h-11 px-3 rounded-lg border border-neutral-200 text-sm bg-white"
          >
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <Button
            onClick={handleBuy}
            data-testid={`merch-buy-${product.product_id}`}
            className="flex-1 h-11 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />Buy Now
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default function Merch() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pending, setPending] = useState(null); // {product, size, quantity}
  const [submitting, setSubmitting] = useState(false);
  const [shipping, setShipping] = useState({
    name: '', email: '', address: '', city: '', province: 'ON', postal_code: ''
  });

  const loadProducts = (attempt = 0) => {
    setLoading(true);
    setLoadError(false);
    fetch(`${API}/api/merch/products`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((d) => { setProducts(d.products || []); setLoading(false); })
      .catch(() => {
        if (attempt < 2) {
          setTimeout(() => loadProducts(attempt + 1), 1500);
        } else {
          setLoadError(true);
          setLoading(false);
        }
      });
  };

  useEffect(() => { loadProducts(); }, []);

  const startCheckout = async () => {
    const required = ['name', 'email', 'address', 'city', 'province', 'postal_code'];
    if (required.some((k) => !shipping[k].trim())) {
      toast.error('Please fill in all shipping fields');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/merch/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: pending.product.product_id,
          size: pending.size,
          quantity: pending.quantity,
          origin_url: window.location.origin,
          shipping
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Checkout failed');
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.message);
      setSubmitting(false);
    }
  };

  const field = (key, label, placeholder, type = 'text') => (
    <div>
      <label className="text-sm font-medium text-neutral-700 mb-1 block">{label}</label>
      <Input
        type={type}
        value={shipping[key]}
        onChange={(e) => setShipping({ ...shipping, [key]: e.target.value })}
        placeholder={placeholder}
        data-testid={`shipping-${key}`}
        className="h-11 rounded-lg"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="pt-32 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="mb-12">
          <p className="text-[#FF3B30] font-semibold uppercase tracking-widest text-sm mb-3">Official Merch</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Wear the Wheel.
          </h1>
          <p className="text-neutral-600 max-w-xl">
            Spinser-branded tees, hoodies and accessories. Every piece features the iconic red wheel —
            rep your favourite home giveaway platform.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#FF3B30]" /></div>
        ) : loadError ? (
          <div className="text-center py-24" data-testid="merch-load-error">
            <p className="text-neutral-600 mb-4">We couldn't load the merch right now.</p>
            <Button onClick={() => loadProducts()} data-testid="merch-retry-btn" className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl h-11 px-8">
              Try Again
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="merch-grid">
            {products.map((p, i) => (
              <ProductCard key={p.product_id} product={p} index={i} onBuy={(product, size, quantity) => setPending({ product, size, quantity })} />
            ))}
          </div>
        )}

        <p className="text-center text-xs text-neutral-400 mt-14">
          Secure checkout powered by Stripe. Prices in CAD. Shipping within Canada.
        </p>
      </div>
      </div>

      <Dialog open={!!pending} onOpenChange={(open) => !open && !submitting && setPending(null)}>
        <DialogContent className="sm:max-w-md" data-testid="shipping-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Shipping Details</DialogTitle>
            <DialogDescription>
              {pending && `${pending.quantity}× ${pending.product.name}${pending.size ? ` (${pending.size})` : ''} — $${(pending.product.price * pending.quantity).toFixed(0)} CAD`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {field('name', 'Full Name', 'Jane Homeowner')}
            {field('email', 'Email', 'you@email.com', 'email')}
            {field('address', 'Street Address', '123 Main St, Unit 4')}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">{field('city', 'City', 'Halton Hills')}</div>
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-1 block">Province</label>
                <select
                  value={shipping.province}
                  onChange={(e) => setShipping({ ...shipping, province: e.target.value })}
                  data-testid="shipping-province"
                  className="h-11 w-full px-2 rounded-lg border border-neutral-200 text-sm bg-white"
                >
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            {field('postal_code', 'Postal Code', 'L7G 1A1')}
            <Button
              onClick={startCheckout}
              disabled={submitting}
              data-testid="shipping-continue-btn"
              className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium mt-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue to Payment'}
            </Button>
            <p className="text-xs text-neutral-400 text-center">You'll complete payment securely on Stripe.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
